import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { groupBy } from 'lodash';
import { Box, Flex, Image, Tag, Icon } from '@chakra-ui/react';

import { TPictures, useGetAllPicturesQuery } from 'core/API/picturesApi';
import PageHeader from 'layout/pageHeader';
import { useTranslation } from 'helpers/languages/translations';
import { Actions, switchFilter } from '../ImagesViewer/ToolBar';
import icons from 'shared/icons';
import DetailForm from './DetailForm';
import ToggleRadioButton from 'components/molecules/ToggleRadioButton';
import { getImageByFilters } from '../ImagesViewer/useGetFilteredPictures';
import { useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';

const isDuplicatePosition = (pictures: TPictures[], position: number) =>
    groupBy(pictures || [], 'position')?.[position]?.length > 1;

const RenderLefPicturestNav = ({
    pictures,
    setSelectedPicture,
    currentPicture,
}: {
    pictures: TPictures[];
    setSelectedPicture: (index: TPictures) => void;
    currentPicture: TPictures;
}) => (
    <Flex direction="column" mr={10} height="500px" overflow="auto" pr="15px">
        {pictures.map((picture, idx) => {
            const positionWarningColor = isDuplicatePosition(pictures, picture.position) ? 'orange.400' : 'inherit';

            return (
                <Box key={picture._id}>
                    {pictures[idx - 1]?.image_type !== picture.image_type && (
                        <Box mb="5" fontWeight="bold" color="gray.600" textTransform="capitalize">
                            {picture.image_type}
                        </Box>
                    )}
                    <Flex
                        mb={10}
                        borderStyle="solid"
                        borderWidth={picture._id === currentPicture?._id ? '2px' : '1px'}
                        borderColor={picture._id === currentPicture?._id ? 'blue.500' : 'gray.600'}
                        boxSize="120px"
                        cursor="pointer"
                        position="relative"
                        justifyContent="center"
                    >
                        <Tag
                            position="absolute"
                            fontSize="sm"
                            padding={1}
                            color={positionWarningColor}
                            top="0px"
                            left="0px"
                        >
                            <Icon as={icons.rank} w="15px" h="15px" color={positionWarningColor} />
                            {picture.position}
                        </Tag>
                        <Image
                            src={picture.image_url}
                            alt={picture.product_name || 'noLabel'}
                            onClick={() => setSelectedPicture(picture)}
                            objectFit="contain"
                            maxW="100%"
                            maxH="100%"
                        />
                    </Flex>
                </Box>
            );
        })}
    </Flex>
);

const SelectedPicture = ({ picture, callback, readonly }: { picture: TPictures; callback: any; readonly: boolean }) => (
    <Flex mr={10} border="1px solid black" boxSize="500px" position="relative" justifyContent="center">
        <Image
            src={picture.image_url}
            alt={`${picture.product_name || 'noLabel'}/${(picture as any)._id}`}
            objectFit="contain"
            maxW="100%"
            maxH="100%"
        />
        <Box position="absolute" top={3} right={3}>
            {picture._id && (
                <Actions
                    currentStatus={/active|archived/.test(picture.status) ? (picture.status as any) : 'other'}
                    checkedPictures={[picture._id]}
                    callback={callback}
                    readonly={readonly}
                />
            )}
        </Box>
    </Flex>
);

const splitPrimarySecondary = (pictures: TPictures[]) => {
    const primarySecondaryGroupedPictures = groupBy(pictures, 'image_type');
    const splitPictures = [
        ...(primarySecondaryGroupedPictures.primary || []),
        ...(primarySecondaryGroupedPictures.secondary || []),
    ];

    return splitPictures;
};

const ImageDetail = () => {
    const { state } = useLocation();
    const picture = state.picture;

    const translate = useTranslation();
    const navigate = useNavigate();

    const ean = useRef(picture.ean);
    const { data, isFetching } = useGetAllPicturesQuery({ ean: ean.current });

    const pictures = data?.data || null;

    const [selectedPicture, setSelectedPicture] = useState<TPictures>(picture);
    const [allPictures, setAllPictures] = useState<TPictures[]>([]);
    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(19);

    const currentStatus = useRef(0);

    const goBack = () => {
        navigate(-1);
    };

    const callbackDeleteOrArchive = () => {
        setAllPictures((pics) => pics.filter((pic) => pic._id !== selectedPicture._id));
    };

    const picturesByStatus = useMemo(
        () => [
            getImageByFilters(pictures || [], { filters: switchFilter.active }) as TPictures[],
            getImageByFilters(pictures || [], { filters: switchFilter.archived }) as TPictures[],
            getImageByFilters(pictures || [], { filters: switchFilter.other }) as TPictures[],
        ],
        [isFetching, pictures],
    );

    const onRadioClick = (index: number) => {
        currentStatus.current = index;
        setSelectedPicture(picturesByStatus[index][0]);
        setAllPictures(picturesByStatus[index]);
    };

    useEffect(() => {
        if (!isFetching) {
            if (picture._id) {
                picturesByStatus.forEach((pics, idx) => {
                    const foundPic = pics.find((pic) => pic._id === picture._id);
                    if (foundPic) {
                        currentStatus.current = idx;
                        setAllPictures(splitPrimarySecondary(picturesByStatus[currentStatus.current]));
                        setSelectedPicture(foundPic);
                        state.picture = {};
                    }
                });
            } else {
                const orderedPictures = splitPrimarySecondary(picturesByStatus[currentStatus.current]);
                setAllPictures(orderedPictures);
                setSelectedPicture(orderedPictures[0]);
            }
        }
    }, [picturesByStatus]);

    const positions = useMemo(() => allPictures.map((pics) => pics.position), [allPictures]);

    return (
        <Box>
            <PageHeader
                pageTitle={`${translate('page.pictures') as string}/${selectedPicture?.ean}`}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            >
                <ToggleRadioButton
                    items={[
                        {
                            content: <Icon as={icons.check} />,
                            onClick: () => onRadioClick(0),
                            tooltip: translate('pictures.active') as string,
                            bubble: picturesByStatus[0].length,
                        },
                        {
                            content: <Icon as={icons.archive} />,
                            onClick: () => onRadioClick(1),
                            tooltip: translate('pictures.archived') as string,
                            bubble: picturesByStatus[1].length,
                        },
                        {
                            content: <Icon as={icons.warning} />,
                            onClick: () => onRadioClick(2),
                            tooltip: `${translate('pictures.upload.no_product')} /
                            ${translate('pictures.upload.wrong_company')}`,
                            bubble: picturesByStatus[2].length,
                        },
                    ]}
                    defaultSelected={currentStatus.current}
                />
            </PageHeader>
            {!allPictures?.length ? null : (
                <Box px="32px">
                    <Flex fontSize="sm" direction="row">
                        <RenderLefPicturestNav
                            pictures={allPictures}
                            setSelectedPicture={setSelectedPicture}
                            currentPicture={selectedPicture}
                        />
                        {selectedPicture && (
                            <>
                                <SelectedPicture
                                    picture={selectedPicture}
                                    callback={callbackDeleteOrArchive}
                                    readonly={readonly}
                                />
                                <DetailForm
                                    selectedPicture={selectedPicture}
                                    positions={positions}
                                    readonly={readonly}
                                />
                            </>
                        )}
                    </Flex>
                </Box>
            )}
        </Box>
    );
};

export default ImageDetail;
